import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, isDevMode, provideAppInitializer, provideZonelessChangeDetection } from '@angular/core';

import { provideHttpClient, withInterceptors } from '@angular/common/http';

import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideServiceWorker } from '@angular/service-worker';
import { Badge } from '@awesome-cordova-plugins/badge/ngx';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { provideState, provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { ConfirmationService, MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { environment } from '../environments/environment';
import { AmmsasErrorHandler } from './app-wrapper/error.handler';
import { jwtInterceptorFn } from './core/api/interceptors/http.interceptorfn';
import { offlineInterceptor } from './core/api/interceptors/offline-interceptor';
import { offlineScopeInterceptor } from './core/api/interceptors/offline-scope.interceptorfn';
import { TokenStorageService } from './core/api/token-storage.service';
import { AuthGuard } from './core/auth/auth.guard';
import { provideRouter, withComponentInputBinding, withRouterConfig } from '@angular/router';
import { AMMSAS_PRESET } from './ammsas-preset';
import { routes } from './app-routes';
import { BandSelectedGuard } from './core/auth/band-selected.guard';
import { AuthStrategy } from './core/auth/strategies/auth.strategy';
import { FirebaseAuthStrategy } from './core/auth/strategies/firebase-auth.strategy';
import { OutboxProcessorService } from './core/db/outbox-processor.service';
import { AuthEffects } from './core/state/effects/auth.effects';
import { BandEffects } from './core/state/effects/band.effects';
import { SettingsEffects } from './core/state/effects/settings.effects';
import { ThemeEffects } from './core/state/effects/theme.effect';
import * as fromCore from './core/state/reducers';
import { CustomDialogService } from './custom-dialog.service';
import { DynamicDialogConfigService } from './dynamic-dialog.service';
import { ORIGINAL_DIALOG_SERVICE } from './tokens';
import { ICalEffects } from './core/state/effects/ical.effects';


export const firebaseConfig = environment.firebase

export function initTokens(tokens: TokenStorageService) {
    return () => tokens.init();
}

export const appConfig: ApplicationConfig = {
    providers: [

        provideZonelessChangeDetection(),
        provideRouter(routes,
            withRouterConfig({
                onSameUrlNavigation: 'reload'
            }),
            withComponentInputBinding()
        ),
        { provide: APP_INITIALIZER, useFactory: initTokens, deps: [TokenStorageService], multi: true },
        {
            provide: APP_INITIALIZER,
            useFactory: (outbox: OutboxProcessorService) => () => outbox.drain(),
            deps: [OutboxProcessorService],
            multi: true
        },
        provideTranslateService({
            lang: 'en_US',
            fallbackLang: 'en_US',
            loader: provideTranslateHttpLoader({
                prefix: './assets/i18n/',
                suffix: '.json'
            })
        }),
        provideAnimationsAsync(),
        providePrimeNG({
            theme: {
                preset: AMMSAS_PRESET,
                options: {
                    prefix: 'p',
                    darkModeSelector: '.dark-mode',
                    cssLayer: false,

                }
            },

        }),
        { provide: AuthStrategy, useClass: FirebaseAuthStrategy }, // ApiAuthStrategy oder FirebaseAuthStrategy
        provideStore({
            router: routerReducer,
        }),
        provideState(fromCore.featureKey, fromCore.reducers),
        provideEffects([
            BandEffects,
            ThemeEffects,
            SettingsEffects,
            AuthEffects,
            ICalEffects

        ]),
        provideServiceWorker('ngsw-worker.js', {
            enabled: !isDevMode(),
            registrationStrategy: 'registerWhenStable:30000'
        }),
        provideStoreDevtools({
            maxAge: 25, // Retains last 25 states
            logOnly: !isDevMode(), // Restrict extension to log-only mode
            autoPause: true, // Pauses recording actions and state changes when the extension window is not open
            trace: false, //  If set to true, will include stack trace for every dispatched action, so you can see it in trace tab jumping directly to that part of code
            traceLimit: 75, // maximum stack trace frames to be stored (in case trace option was provided as true)
            connectInZone: true // If set to true, the connection is established within the Angular zone
        }),
        provideRouterStore(),
        provideStoreDevtools({
            maxAge: 25,
            logOnly: !isDevMode(),
        }),
        provideRouterStore(),

        { provide: ORIGINAL_DIALOG_SERVICE, useClass: DialogService },
        provideHttpClient(withInterceptors([
            jwtInterceptorFn,
            offlineScopeInterceptor,
            offlineInterceptor,
        ])),
        MessageService,
        ConfirmationService,
        { provide: DialogService, useClass: CustomDialogService },
        {
            provide: DynamicDialogConfig,
            useFactory: (configService: DynamicDialogConfigService) => configService.getConfig(),
            deps: [DynamicDialogConfigService],
        },
        { provide: AuthGuard, useClass: AuthGuard },
        { provide: BandSelectedGuard, useClass: BandSelectedGuard },
        { provide: ErrorHandler, useClass: AmmsasErrorHandler },
        Badge
    ]
};
